import { parseISO } from 'date-fns';
import type { CarePlan, Condition, Goal, Task } from '@medplum/fhirtypes';
import type { TimelineRange } from '../components/atoms/Timeline';

// Triangle markers (fax timeline items) are cut with clip-path: up = outbound, down = inbound.
// Shared by the Timeline dot renderer and the legend glyphs in consumers.
export const TRIANGLE_UP_CLIP_CLASS = '[clip-path:polygon(50%_0%,100%_100%,0%_100%)]';
export const TRIANGLE_DOWN_CLIP_CLASS = '[clip-path:polygon(0%_0%,100%_0%,50%_100%)]';

/**
 * Items whose ISO date (via `getDate`) is at or before `cutoff` — the shared reveal filter behind
 * {@link observationsThroughTime} (measurements) and the adherence charts' occurrence/weekly points.
 * A null cutoff (live) returns the input untouched; an item with a missing or unparseable date is
 * kept rather than dropped, so scrubbing never hides data we can't date.
 */
export const itemsThroughTime = <T>(items: T[], getDate: (item: T) => string | undefined, cutoff: Date | null): T[] => {
  if (!cutoff) return items;
  const limit = cutoff.getTime();
  return items.filter((item) => {
    const t = parseISO(getDate(item) ?? '').getTime();
    return Number.isNaN(t) || t <= limit;
  });
};

/**
 * Observations at or before `cutoff` — the temporal filter that makes charts "reveal" as the timeline
 * is scrubbed. A null cutoff (live, not scrubbing) returns the input untouched. Undated observations
 * are kept rather than dropped, so a missing `effectiveDateTime` never hides real data.
 */
export const observationsThroughTime = <T extends { effectiveDateTime?: string }>(
  observations: T[],
  cutoff: Date | null,
): T[] => itemsThroughTime(observations, (o) => o.effectiveDateTime, cutoff);

type DatedResource = Task | CarePlan | Goal | Condition;

/**
 * Best-effort instant a care-plan resource was *added to the plan*, as epoch ms (undefined when we
 * can't date it). Uses the creation-ish field per type — deliberately NOT `meta.lastUpdated`, which
 * moves on every edit and would wrongly hide a long-standing item that was merely touched recently.
 */
export const resourceAddedAt = (resource: DatedResource): number | undefined => {
  const iso =
    resource.resourceType === 'Task'
      ? resource.authoredOn
      : resource.resourceType === 'CarePlan'
        ? (resource.created ?? resource.period?.start)
        : resource.resourceType === 'Goal'
          ? resource.startDate
          : (resource.recordedDate ?? resource.onsetDateTime); // Condition
  if (!iso) return undefined;
  const ms = parseISO(iso).getTime();
  return Number.isNaN(ms) ? undefined : ms;
};

/**
 * Whether a resource had been added by the scrubbed instant — the timeline reveal's core predicate.
 * A null cutoff (live) or an undatable resource always counts as present, so scrubbing never hides
 * something we can't positively date as added later.
 */
export const existedAt = (resource: DatedResource, cutoff: Date | null): boolean => {
  if (!cutoff) return true;
  const added = resourceAddedAt(resource);
  return added === undefined || added <= cutoff.getTime();
};

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;
// Playback advances at a steady real rate — a longer window takes longer — deliberate but watchable.
// These are the 1× values; the speed picker scales the result (see the play loop).
const PLAYBACK_MS_PER_WEEK = 500;
// Rails so a 1× sweep lands in a ~5–20s band: a short/zoomed window still animates for a beat, and a
// decade-spanning one stays bounded. The speed picker divides this (2× = half the time, 0.25× = 4×).
const MIN_PLAYBACK_MS = 5000;
const MAX_PLAYBACK_MS = 20000;

/** Convert a Date / ISO string / nullish to epoch ms (NaN for nullish or unparseable input). */
export const toEpoch = (value: Date | string | null | undefined): number => {
  if (value === null || value === undefined) return NaN;
  return value instanceof Date ? value.getTime() : parseISO(value).getTime();
};

/** Wall-clock duration of a play-through at a steady rate, floored/capped by the rails so short windows
 * still animate for a beat and long ones stay a bounded blur. */
export const playbackDurationMs = (range: TimelineRange): number => {
  const weeks = (range.end.getTime() - range.start.getTime()) / MS_PER_WEEK;
  return Math.min(MAX_PLAYBACK_MS, Math.max(MIN_PLAYBACK_MS, weeks * PLAYBACK_MS_PER_WEEK));
};

/** Position of `current` within `range`, clamped to 0–1. */
export const progressFraction = (current: Date, range: TimelineRange): number => {
  const span = range.end.getTime() - range.start.getTime();
  if (span <= 0) return 0;
  return Math.min(1, Math.max(0, (current.getTime() - range.start.getTime()) / span));
};

/** The instant at fraction `f` (0–1, clamped) across `range`. Inverse of {@link progressFraction}. */
export const dateAtFraction = (fraction: number, range: TimelineRange): Date => {
  const span = range.end.getTime() - range.start.getTime();
  const clamped = Math.min(1, Math.max(0, fraction));
  return new Date(range.start.getTime() + clamped * span);
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** A topic being added to the plan, or an item (measurement/med/lifestyle/task) added to a topic. */
export type PlanChangeKind = 'topicAdded' | 'itemAdded';

/** A single dated plan-change event, before same-day clustering. */
export interface PlanChangeEntry {
  /** Epoch ms the change happened (item → Task.authoredOn; topic → CarePlan.created or earliest item). */
  addedAt: number;
  /** Human label: the item's description, or the topic's title for a `topicAdded` entry. */
  label: string;
  kind: PlanChangeKind;
  topicId?: string;
  topicTitle?: string;
}

/** Same-day plan changes collapsed into one timeline marker. */
export interface PlanChangeCluster {
  /** Stable key across renders (derived from the UTC-day bucket). */
  id: string;
  /** Representative instant — the earliest change in the bucket. */
  date: number;
  /** Number of items added (topic-added entries aren't counted, so "new topic + 3 items" reads 3). */
  count: number;
  /** Every change in the bucket, ordered by time — drives the marker's tooltip. */
  entries: PlanChangeEntry[];
  /** True only for the earliest cluster: where the plan began. */
  isPlanStart: boolean;
}

/** The subset of `fhirCarePlan.getMaster` output this reads — structurally typed to stay decoupled. */
export interface PlanChangeSource {
  masterTasks: Task[];
  carePlans: { carePlan: CarePlan; tasks: Task[] }[];
  /**
   * Original "added to plan" instant (ISO) per active Task id, for items edited after introduction.
   * An edit (e.g. rescheduling a measurement) is a cancel-old + create-new, so the active Task's
   * `authoredOn` is the edit date; this override dates the change to the real introduction instead.
   * Optional so callers that don't supply it fall back to each Task's own `authoredOn`.
   */
  introducedAtByTaskId?: Record<string, string>;
}

/**
 * A Task's "added to plan" instant (epoch ms), preferring the introduced-at override — which
 * back-dates an item edited after introduction (a cancel-old + create-new reschedule) to its original
 * introduction — over the Task's own `authoredOn`. Returns undefined when the Task can't be dated.
 */
export const taskAddedAt = (task: Task, introducedAtByTaskId?: Record<string, string>): number | undefined => {
  const overrideIso = task.id ? introducedAtByTaskId?.[task.id] : undefined;
  if (overrideIso) {
    const ms = parseISO(overrideIso).getTime();
    if (!Number.isNaN(ms)) return ms;
  }
  return resourceAddedAt(task);
};

/**
 * Whether a Task had been added to the plan by the scrubbed instant, honoring the introduced-at
 * override so a rescheduled measurement stays visible back to its original introduction (not just its
 * latest edit). A null cutoff (live) or an undatable Task always counts as present — mirrors
 * {@link existedAt} but for the back-dated Task instant.
 */
export const taskExistedAt = (
  task: Task,
  cutoff: Date | null,
  introducedAtByTaskId?: Record<string, string>,
): boolean => {
  if (!cutoff) return true;
  const added = taskAddedAt(task, introducedAtByTaskId);
  return added === undefined || added <= cutoff.getTime();
};

/**
 * Flatten a care plan's master data into dated plan-change entries. Only `Task.authoredOn` is a
 * trustworthy "added to plan" date today, so every item is a Task and a topic is dated by its own
 * `CarePlan.created` (falling back to its earliest item for topics created before `created` was
 * populated). Tasks we can't date, and topics with no dated tasks, are skipped rather than guessed.
 */
export const buildPlanChangeEntries = (source: PlanChangeSource): PlanChangeEntry[] => {
  const entries: PlanChangeEntry[] = [];
  const overrides = source.introducedAtByTaskId;

  for (const { carePlan, tasks } of source.carePlans) {
    const topicId = carePlan.id;
    const topicTitle = carePlan.title;
    let earliest: number | undefined;
    for (const task of tasks) {
      const addedAt = taskAddedAt(task, overrides);
      if (addedAt === undefined) continue;
      if (earliest === undefined || addedAt < earliest) earliest = addedAt;
      entries.push({ addedAt, label: task.description ?? '', kind: 'itemAdded', topicId, topicTitle });
    }
    const topicAddedAt = resourceAddedAt(carePlan) ?? earliest;
    if (topicAddedAt !== undefined) {
      entries.push({ addedAt: topicAddedAt, label: topicTitle ?? '', kind: 'topicAdded', topicId, topicTitle });
    }
  }

  for (const task of source.masterTasks) {
    const addedAt = taskAddedAt(task, overrides);
    if (addedAt === undefined) continue;
    entries.push({ addedAt, label: task.description ?? '', kind: 'itemAdded' });
  }

  return entries;
};

/**
 * Group plan-change entries into markers bucketed by UTC calendar day (plan updates land in bursts, so
 * a day of changes reads as one marker). Bucketing on `addedAt / MS_PER_DAY` keeps it timezone-
 * independent and deterministic in tests. The earliest cluster is flagged `isPlanStart`.
 */
export const clusterPlanChanges = (entries: PlanChangeEntry[], opts?: { bucketMs?: number }): PlanChangeCluster[] => {
  if (entries.length === 0) return [];
  const bucketMs = opts?.bucketMs ?? MS_PER_DAY;

  const byBucket = new Map<number, PlanChangeEntry[]>();
  for (const entry of entries) {
    const bucket = Math.floor(entry.addedAt / bucketMs);
    const group = byBucket.get(bucket);
    if (group) group.push(entry);
    else byBucket.set(bucket, [entry]);
  }

  const clusters = [...byBucket].map(([bucket, group]): PlanChangeCluster => {
    const sorted = [...group].sort((a, b) => a.addedAt - b.addedAt);
    return {
      id: `pc-${bucket}`,
      date: sorted[0]!.addedAt,
      count: sorted.filter((e) => e.kind === 'itemAdded').length,
      entries: sorted,
      isPlanStart: false,
    };
  });
  clusters.sort((a, b) => a.date - b.date);
  if (clusters[0]) clusters[0].isPlanStart = true;
  return clusters;
};
